import { useState, useEffect, useCallback } from 'react';
import { 
  signInWithPopup, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  GoogleAuthProvider,
  sendEmailVerification
} from 'firebase/auth';
import type { User as FirebaseUser } from 'firebase/auth';
import { auth } from '../firebase/config';
import { useAuth } from './useAuth';
import type { User } from '../types';

// Module-level state to prevent multiple listeners and operations
let globalAuthListenerSetup = false;
let globalAuthInProgress = false;
let currentProcessingUid: string | null = null;
let authPromise: Promise<unknown> | null = null;
let adminLoginInProgress = false;

// Debug function to track globalAuthInProgress changes
const setGlobalAuthInProgress = (value: boolean, _source: string) => {
  globalAuthInProgress = value;
};

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.ppl.contact';

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  apiKey: string;
  permissions: string[];
}

export interface EmailAuthCredentials {
  email: string;
  password: string;
}

export interface EmailSignUpCredentials extends EmailAuthCredentials {
  displayName: string;
}

const googleProvider = new GoogleAuthProvider();

export const useFirebaseAuth = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { login } = useAuth();

  // Check if we have stored user data after admin login
  useEffect(() => {
    const checkStoredUser = () => {
      const storedUserData = localStorage.getItem('auth_user_data');
      if (storedUserData && !user) {
        try {
          const userData = JSON.parse(storedUserData);
          // Check for admin users (either admin-direct or admin-system)
          if (userData.uid && (userData.uid.includes('admin') || userData.permissions?.includes('admin'))) {

            setUser(userData);
          }
        } catch (error) {

        }
      }
    };

    checkStoredUser();
  }, []);

  // Debug: Track user state changes
  useEffect(() => {

  }, [user]);

  // Handle Google Sign In
  const signInWithGoogle = useCallback(async (isSignUp: boolean = false) => {
    if (globalAuthInProgress) {

      return false;
    }

    try {
      setError(null);
      setLoading(true);
      setGlobalAuthInProgress(true, 'signInWithGoogle');
      
      const result = await signInWithPopup(auth, googleProvider);
      const firebaseUser = result.user;
      
      // Create or get user from our backend
      const userData = await createOrGetUser(firebaseUser, isSignUp);
      
      if (userData) {
        setUser(userData);
        return true;
      } else {
        setError('Failed to authenticate with the server');
        return false;
      }
    } catch (error: unknown) {

      if (error && typeof error === 'object' && 'code' in error && error.code === 'auth/popup-closed-by-user') {
        // User cancelled - don't show error, just reset state
        setError(null);
      } else if (error && typeof error === 'object' && 'code' in error && error.code === 'auth/cancelled-popup-request') {
        // User cancelled - don't show error, just reset state
        setError(null);
      } else if (error && typeof error === 'object' && 'code' in error && error.code === 'auth/popup-blocked') {
        setError('Sign-in popup was blocked. Please allow popups for this site.');
      } else {
        setError('Failed to sign in with Google. Please try again.');
      }
      return false;
    } finally {
      setLoading(false);
      setGlobalAuthInProgress(false, 'signInWithGoogle');
    }
  }, []);

  // Handle Email/Password Sign In
  const signInWithEmail = useCallback(async (credentials: EmailAuthCredentials) => {
    if (globalAuthInProgress) {

      return false;
    }

    try {
      setError(null);
      setLoading(true);
      setGlobalAuthInProgress(true, 'signInWithEmail');
      
      const result = await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
      const firebaseUser = result.user;
      
      // Check if email is verified
      if (!firebaseUser.emailVerified) {
        setError('Please verify your email address before signing in. Check your inbox (including spam folder) for a verification link.');
        return false;
      }
      
      // Create or get user from our backend
      const userData = await createOrGetUser(firebaseUser, false);
      
      if (userData) {
        setUser(userData);
        return true;
      } else {
        setError('Failed to authenticate with the server');
        return false;
      }
    } catch (error: unknown) {

      if (error && typeof error === 'object' && 'code' in error && error.code === 'auth/user-not-found') {
        setError('No account found with this email address');
      } else if (error && typeof error === 'object' && 'code' in error && error.code === 'auth/wrong-password') {
        setError('Incorrect password');
      } else if (error && typeof error === 'object' && 'code' in error && error.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else {
        setError('Failed to sign in. Please try again.');
      }
      return false;
    } finally {
      setLoading(false);
      setGlobalAuthInProgress(false, 'signInWithEmail');
    }
  }, []);

  // Handle Email/Password Sign Up
  const signUpWithEmail = useCallback(async (credentials: EmailSignUpCredentials) => {
    if (globalAuthInProgress) {

      return { success: false, requiresVerification: false };
    }

    try {
      setError(null);
      setLoading(true);
      setGlobalAuthInProgress(true, 'signUpWithEmail');
      
      const result = await createUserWithEmailAndPassword(auth, credentials.email, credentials.password);
      const firebaseUser = result.user;
      
      // Send email verification with action code settings

      try {
        await sendEmailVerification(firebaseUser, {
          url: `${window.location.origin}?verified=true`, // Return to the app after verification
          handleCodeInApp: false
        });

      } catch (emailError: unknown) {

        // Still continue with account creation but show a warning
        throw new Error('Account created but failed to send verification email. Please try the "Resend" option.');
      }
      
      // Sign out the user immediately since they need to verify their email
      await signOut(auth);
      

      return { success: true, requiresVerification: true };
      
    } catch (error: unknown) {

      if (error && typeof error === 'object' && 'code' in error && error.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists');
      } else if (error && typeof error === 'object' && 'code' in error && error.code === 'auth/weak-password') {
        setError('Password is too weak');
      } else if (error && typeof error === 'object' && 'code' in error && error.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else {
        setError('Failed to create account. Please try again.');
      }
      return { success: false, requiresVerification: false };
    } finally {
      setLoading(false);
      setGlobalAuthInProgress(false, 'signUpWithEmail');
    }
  }, []);

  // Create or get user from our backend - SINGLE ATTEMPT with proper locking
  const createOrGetUser = async (firebaseUser: FirebaseUser, isSignUp: boolean): Promise<AuthUser | null> => {
    const uid = firebaseUser.uid;
    
    // Check if we're already processing this UID
    if (currentProcessingUid === uid && authPromise) {

      try {
        const result = await authPromise;
        return result as AuthUser | null;
      } catch (error) {

        throw error;
      }
    }
    
    // Check if auth is already in progress globally for a DIFFERENT UID
    if (globalAuthInProgress && currentProcessingUid !== uid && currentProcessingUid !== null) {
      return null;
    }
    

    
    // Set up the promise and tracking
    currentProcessingUid = uid;
    authPromise = (async () => {
      try {
        // Force token refresh to ensure we have a fresh token
        const idToken = await firebaseUser.getIdToken(true);
        
        const endpoint = isSignUp ? '/api/auth/signup' : '/api/auth/login';

        
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            uid: firebaseUser.uid
          })
        });

        if (response.ok) {
          const userData = await response.json();

          
          // Update AuthContext with the new API key and user data
          if (userData.apiKey) {

            // Convert AuthUser to User type format
            const userForAuthContext = {
              api_key: userData.apiKey,
              name: userData.displayName || userData.email || 'User',
              permissions: userData.permissions || [],
              is_active: true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
            await login(userData.apiKey, userForAuthContext);
          }
          
          return {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            apiKey: userData.apiKey,
            permissions: userData.permissions || []
          };
        } else {
          const errorData = await response.json();
          const errorMessage = errorData.detail || errorData.message || 'Authentication failed';

          // Handle specific status codes with user-friendly messages
          if (response.status === 429) {
            throw new Error('Another signup or login is already in progress. Please wait a moment and try again.');
          } else if (response.status === 409) {
            throw new Error(errorMessage); // "An account with this email already exists..."
          } else if (response.status === 403) {
            throw new Error(errorMessage); // "This account has been deactivated..."
          } else {
            throw new Error(errorMessage);
          }
        }
      } catch (error) {

        throw error;
      }
    })();
    
    try {
      const result = await authPromise;
      return result as AuthUser | null;
    } finally {
      // Clean up tracking
      if (currentProcessingUid === uid) {
        currentProcessingUid = null;
        authPromise = null;
      }
    }
  };

  // Sign out
  const signOutUser = useCallback(async () => {
    try {
      await signOut(auth);
      setUser(null);
      setGlobalAuthInProgress(false, 'signOutUser');
      // Reset tracking
      currentProcessingUid = null;
      authPromise = null;
      
      // Clear any stored API keys from localStorage
      localStorage.removeItem('ppl_contact_api_key');
      localStorage.removeItem('auth_user_data');
      
      // Clear saved filters when logging out
      try {
        localStorage.removeItem('ppl_contact_filters');
      } catch (filterError) {
        console.warn('Failed to clear saved filters on logout:', filterError);
      }

    } catch (error) {

    }
  }, []);

  // Listen for auth state changes - TRULY SINGLETON with proper sequencing
  useEffect(() => {
    if (globalAuthListenerSetup) {

      return;
    }
    
    globalAuthListenerSetup = true;

    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {

      
      if (firebaseUser) {
        const uid = firebaseUser.uid;
        
        // Check if we're already processing this UID
        if (currentProcessingUid === uid) {

          return;
        }
        
        // Check if auth is already in progress globally for a DIFFERENT UID
        if (globalAuthInProgress && currentProcessingUid !== uid && currentProcessingUid !== null) {

          return;
        }
        

        
        // Check if email is verified before proceeding
        if (!firebaseUser.emailVerified) {

          setGlobalAuthInProgress(false, 'onAuthStateChanged');
          // Sign out immediately if email is not verified
          await signOut(auth);
          return;
        }
        
        setGlobalAuthInProgress(true, 'onAuthStateChanged');
        
        try {
          const userData = await createOrGetUser(firebaseUser, false);
          if (userData) {

            setUser(userData);
          } else {

          }
        } catch (error) {

          // If backend auth fails, sign out from Firebase
          await signOut(auth);
        } finally {
          setGlobalAuthInProgress(false, 'onAuthStateChanged');
        }
      } else {

        // Don't reset user state if admin login is in progress or if we have an admin user
        if (adminLoginInProgress) {

          return;
        }
        
        // Check if current user is admin (has special admin UID)
        setUser(currentUser => {
          if (currentUser?.uid && (currentUser.uid.includes('admin') || currentUser.permissions?.includes('admin'))) {

            return currentUser;
          } else {

            return null;
          }
        });
        setGlobalAuthInProgress(false, 'onAuthStateChanged');
        // Reset tracking
        currentProcessingUid = null;
        authPromise = null;
        
        // Clear any stored API keys from localStorage
        localStorage.removeItem('ppl_contact_api_key');
        localStorage.removeItem('auth_user_data');

      }
      setLoading(false);
    });

    return () => {

      unsubscribe();
      globalAuthListenerSetup = false;
    };
  }, []);

  // Resend email verification
  const resendVerificationEmail = useCallback(async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      
      // Sign in temporarily to get the user object
      const result = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = result.user;
      
      if (firebaseUser.emailVerified) {
        setError('Email is already verified. You can now sign in.');
        await signOut(auth);
        return false;
      }
      
      // Send verification email
      await sendEmailVerification(firebaseUser, {
        url: `${window.location.origin}?verified=true`,
        handleCodeInApp: false
      });
      
      // Sign out immediately
      await signOut(auth);
      
      return true;
    } catch (error: unknown) {

      if (error && typeof error === 'object' && 'code' in error && error.code === 'auth/user-not-found') {
        setError('No account found with this email address');
      } else if (error && typeof error === 'object' && 'code' in error && error.code === 'auth/wrong-password') {
        setError('Incorrect password');
      } else {
        setError('Failed to resend verification email. Please try again.');
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle Admin Login (bypasses Firebase)
  const signInAsAdmin = useCallback(async (username: string, password: string) => {
    if (globalAuthInProgress) {

      return false;
    }

    try {
      setError(null);
      setLoading(true);
      adminLoginInProgress = true;
      setGlobalAuthInProgress(true, 'signInAsAdmin');

      const response = await fetch(`${API_BASE_URL}/api/auth/admin-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username,
          password
        })
      });

      if (response.ok) {
        const authData = await response.json();
        if (authData.success) {
          const userData = {
            apiKey: authData.apiKey,
            permissions: authData.permissions,
            displayName: authData.user.displayName,
            email: authData.user.email,
            photoURL: authData.user.photoURL,
            uid: authData.user.uid || 'admin-direct'
          };
          

          
          // Use the login function from useAuth to set the user
          if (login) {
            const userForAuth: User = {
              api_key: authData.apiKey,
              name: authData.user.displayName || authData.user.email || 'Admin User',
              permissions: authData.permissions || [],
              is_active: true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
            login(authData.apiKey, userForAuth);
          }

          setUser(userData);
          
          // Store admin user data for persistence across page reloads
          localStorage.setItem('auth_user_data', JSON.stringify(userData));

          
          // Force App component to re-evaluate by triggering a small delay
          setTimeout(() => {

            // Force a complete page reload to ensure clean state
            window.location.reload();
          }, 100);
          return true;
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Admin login failed');
        return false;
      }
    } catch (error: unknown) {

      setError('Failed to login as admin. Please try again.');
      return false;
    } finally {
      setLoading(false);
      adminLoginInProgress = false;
      setGlobalAuthInProgress(false, 'signInAsAdmin');
    }
    
    return false;
  }, [login]);

  const isAuthenticated = !!user;

  return {
    user,
    loading,
    error,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    resendVerificationEmail,
    signOutUser,
    signInAsAdmin,
    isAuthenticated
  };
}; 